import { useEffect, useState } from "react";
import { Zap, Scale, Rocket, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useOwnClientId } from "@/hooks/useOwnClientId";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type Mode = "fast" | "balanced" | "premium";

const MODES: {
  id: Mode;
  title: string;
  icon: typeof Zap;
  emoji: string;
  desc: string;
  accent: string;
}[] = [
  {
    id: "fast",
    title: "Fast",
    icon: Zap,
    emoji: "⚡",
    desc: "Fastest and most economical.",
    accent: "from-amber-400/20 to-yellow-500/10",
  },
  {
    id: "balanced",
    title: "Balanced",
    icon: Scale,
    emoji: "⚖️",
    desc: "Best quality-to-cost ratio.",
    accent: "from-sky-400/20 to-indigo-500/10",
  },
  {
    id: "premium",
    title: "Premium",
    icon: Rocket,
    emoji: "🚀",
    desc: "Highest quality.",
    accent: "from-fuchsia-400/20 to-violet-500/10",
  },
];

interface Props {
  clientIds?: string[];
}

const SettingsTab = ({ clientIds = [] }: Props) => {
  const { ownClientId } = useOwnClientId("SettingsTab");
  const activeClientId = ownClientId ?? clientIds[0] ?? null;

  const [mode, setMode] = useState<Mode>("fast");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Mode | null>(null);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("clients")
        .select("ai_mode_default")
        .eq("id", activeClientId)
        .maybeSingle();
      if (cancelled) return;
      const saved = (data as any)?.ai_mode_default as Mode | null;
      if (saved === "fast" || saved === "balanced" || saved === "premium") {
        setMode(saved);
      } else {
        setMode("fast");
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeClientId]);

  const selectMode = async (next: Mode) => {
    if (!activeClientId || next === mode) return;
    setSaving(next);
    const prev = mode;
    setMode(next);
    const { error } = await supabase
      .from("clients")
      .update({ ai_mode_default: next } as any)
      .eq("id", activeClientId);
    setSaving(null);
    if (error) {
      setMode(prev);
      toast({
        title: "Couldn't save",
        description: "Your AI Quality Mode wasn't updated. Please try again.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "AI Quality Mode updated",
      description: `Set to ${next.charAt(0).toUpperCase() + next.slice(1)}.`,
    });
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-8 md:py-12">
      <div className="mb-8">
        <h1 className="text-[26px] font-[800] tracking-[-0.6px] text-foreground md:text-[32px]">
          Workspace Settings
        </h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Manage how OrbitRank works for your workspace.
        </p>
      </div>

      <section className="rounded-[18px] border border-border bg-card p-6 shadow-[0_1px_0_hsl(var(--border))] md:p-8">
        <div className="mb-6 flex flex-col gap-1">
          <div className="text-[10px] font-[800] uppercase tracking-[1.6px] text-primary/80">
            AI Quality Mode
          </div>
          <h2 className="text-[20px] font-[700] tracking-[-0.3px] text-foreground">
            Speed, efficiency, and output quality
          </h2>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            Choose how OrbitRank balances speed, efficiency, and output quality
            across AI-powered actions.
          </p>
        </div>

        <div
          role="radiogroup"
          aria-label="AI Quality Mode"
          className={cn(
            "grid gap-3 md:grid-cols-3",
            loading && "opacity-60 pointer-events-none",
          )}
        >
          {MODES.map((m) => {
            const Icon = m.icon;
            const selected = mode === m.id;
            const isSaving = saving === m.id;
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={loading || !!saving}
                onClick={() => selectMode(m.id)}
                className={cn(
                  "group relative overflow-hidden rounded-[16px] border p-5 text-left transition-all duration-300",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                  selected
                    ? "border-primary/60 bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary)/0.5),0_8px_30px_-12px_hsl(var(--primary)/0.45)]"
                    : "border-border bg-background hover:border-primary/30 hover:bg-accent/40",
                )}
              >
                <div
                  className={cn(
                    "pointer-events-none absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300",
                    m.accent,
                    selected ? "opacity-100" : "group-hover:opacity-60",
                  )}
                />
                <div className="relative">
                  <div className="mb-3 flex items-center justify-between">
                    <div
                      className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-[11px] border transition-colors",
                        selected
                          ? "border-primary/40 bg-primary/15 text-primary"
                          : "border-border bg-card text-foreground",
                      )}
                    >
                      <Icon size={18} strokeWidth={2.25} />
                    </div>
                    <div
                      className={cn(
                        "flex h-6 w-6 items-center justify-center rounded-full border transition-all",
                        selected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-transparent text-transparent",
                      )}
                      aria-hidden
                    >
                      <Check size={14} strokeWidth={3} />
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[15px] leading-none">{m.emoji}</span>
                    <span className="text-[16px] font-[800] tracking-[-0.2px] text-foreground">
                      {m.title}
                    </span>
                  </div>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                    {m.desc}
                  </p>

                  <div className="mt-4 flex items-center gap-1.5 text-[10px] font-[700] uppercase tracking-[1.2px]">
                    {selected ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-primary">
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full bg-primary"
                          style={{ animation: "pulseRing 2s infinite" }}
                        />
                        {isSaving ? "Saving…" : "Selected"}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/60">
                        {isSaving ? "Saving…" : "Tap to select"}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <p className="mt-5 text-[11.5px] leading-relaxed text-muted-foreground/80">
          Your selection applies to every AI-powered action in this workspace.
          Specific actions may override this default when a different level of
          quality is required.
        </p>
      </section>
    </div>
  );
};

export default SettingsTab;
